import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Excel export for the Fuel Monthly Summary (2026-10-09). The generic list export
// wrote every figure as text, repeated the vehicle type on each row and could not
// merge, so the sheet looked nothing like the screen. This one lays the sheet out
// like the on-screen grid:
//   Vehicle | Assigned to | Card | Entry | Opening | 1 .. 31 | Total | Litres | Closing
// - one bold vehicle-type heading row (merged across), then each vehicle with one
//   line per entry type, Vehicle/Card/Opening/Closing merged down its lines;
// - a subtotal block per type and a grand-total block, same shape;
// - every amount is a real number (#,##0.00), so it right-aligns and sums.
// The bundled SheetJS community build cannot freeze panes or colour cells.

const MONEY = "#,##0.00";

export const exportFuelMonthToExcel = ({
  companyName = "TEXAS ALARMS (K) LTD",
  title,
  subtitleLines = [],
  month, // dayjs, first of the month
  days, // [1..n]
  groups, // buildGrid().groups
  grand, // buildGrid().grand
  entryOrder,
  entryLabel,
  typeLabel,
  closingOf,
  assignedOf = () => "", // (vehicle) => employee name the vehicle is assigned to
  fileName,
}) => {
  const ws = {};
  const merges = [];
  const C_VEHICLE = 0;
  const C_ASSIGNED = 1;
  const C_CARD = 2;
  const C_ENTRY = 3;
  const C_OPENING = 4;
  const C_DAY1 = 5;
  const C_TOTAL = C_DAY1 + days.length;
  const C_LITRES = C_TOTAL + 1;
  const C_CLOSING = C_LITRES + 1;
  const LAST = C_CLOSING;

  const put = (r, c, v, z = MONEY) => {
    if (v === null || v === undefined || v === "") return;
    ws[XLSX.utils.encode_cell({ r, c })] = typeof v === "number" ? { t: "n", v, z } : { t: "s", v: String(v) };
  };
  const mergeDown = (r, c, n) => {
    if (n > 1) merges.push({ s: { r, c }, e: { r: r + n - 1, c } });
  };

  let r = 0;
  put(r++, 0, companyName);
  put(r++, 0, title);
  subtitleLines.forEach((l) => put(r++, 0, l));
  r += 1;

  // Header: day number plus its weekday ("1 Thu"), the same cue the screen gives.
  ["Vehicle", "Assigned to", "Card", "Entry", "Opening"].forEach((h, i) => put(r, i, h));
  days.forEach((d, i) => put(r, C_DAY1 + i, `${d} ${month.date(d).format("ddd")}`));
  put(r, C_TOTAL, "Total");
  put(r, C_LITRES, "Litres");
  put(r, C_CLOSING, "Closing");
  r += 1;

  // One block of lines (a vehicle, or a subtotal/grand total): Vehicle, Card,
  // Opening and Closing written once on the first line and merged down.
  const writeBlock = ({ label, assigned, card, opening, lines, closing, litresOf }) => {
    const shown = entryOrder.filter((e) => lines[e] && lines[e].total > 0);
    const rowsFor = shown.length ? shown : [null];
    const start = r;
    rowsFor.forEach((e) => {
      put(r, C_ENTRY, e ? entryLabel[e] : "-");
      if (e) {
        const line = lines[e];
        days.forEach((d, i) => {
          const amount = Number(line.days[d]?.amount || 0);
          if (amount > 0) put(r, C_DAY1 + i, amount);
        });
        put(r, C_TOTAL, Number(line.total || 0));
        if (e === "FUEL" && litresOf) put(r, C_LITRES, litresOf, MONEY);
      }
      r += 1;
    });
    put(start, C_VEHICLE, label);
    put(start, C_ASSIGNED, assigned);
    put(start, C_CARD, card);
    put(start, C_OPENING, Number(opening || 0));
    put(start, C_CLOSING, Number(closing || 0));
    [C_VEHICLE, C_ASSIGNED, C_CARD, C_OPENING, C_CLOSING].forEach((c) => mergeDown(start, c, rowsFor.length));
  };

  groups.forEach((g) => {
    put(r, 0, `${typeLabel(g.vehicleType)} (${g.vehicles.length} ${g.vehicles.length === 1 ? "vehicle" : "vehicles"})`);
    merges.push({ s: { r, c: 0 }, e: { r, c: LAST } });
    r += 1;
    g.vehicles.forEach((v) =>
      writeBlock({
        label: v.regNo,
        assigned: assignedOf(v),
        card: [...v.cards].join(", "),
        opening: v.opening,
        lines: v.lines,
        closing: closingOf(v.opening, v.lines),
        litresOf: v.lines.FUEL?.litres || 0,
      })
    );
    writeBlock({
      label: `${typeLabel(g.vehicleType)} subtotal`,
      opening: g.totals.opening,
      lines: g.totals.lines,
      closing: closingOf(g.totals.opening, g.totals.lines),
      litresOf: g.vehicles.reduce((t, v) => t + (v.lines.FUEL?.litres || 0), 0),
    });
    r += 1;
  });

  writeBlock({
    label: "GRAND TOTAL",
    opening: grand.opening,
    lines: grand.lines,
    closing: closingOf(grand.opening, grand.lines),
    litresOf: groups.reduce((t, g) => t + g.vehicles.reduce((s, v) => s + (v.lines.FUEL?.litres || 0), 0), 0),
  });

  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: r - 1, c: LAST } });
  ws["!merges"] = merges;
  ws["!cols"] = [
    { wch: 14 }, // Vehicle
    { wch: 22 }, // Assigned to
    { wch: 20 }, // Card
    { wch: 11 }, // Entry
    { wch: 13 }, // Opening
    ...days.map(() => ({ wch: 10 })),
    { wch: 13 }, // Total
    { wch: 10 }, // Litres
    { wch: 13 }, // Closing
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, month.format("MMM YYYY"));
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileName}.xlsx`);
};
